import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../../components/ui/table';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 100;

const fmt = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// Detail view for the LoanBook Engine's shadow (observation-only) rows —
// what the new engine computed vs what the Excel-fed balance_timeseries/
// interest_timeseries already had, for the same loan/date. Read-only.
const AdminLoanbookShadowPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [source, setSource] = useState('');
  const [driftOnly, setDriftOnly] = useState(true);
  const [loanIdFilter, setLoanIdFilter] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/loanbook-engine/shadow-dates`, { headers })
      .then((r) => {
        setDates(r.data.dates || []);
        if (r.data.dates?.length) setSelectedDate(r.data.dates[0]);
      })
      .catch(() => toast.error('Failed to load available dates'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { drift_only: driftOnly, limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (selectedDate) params.date = selectedDate;
      if (source) params.source = source;
      if (loanIdFilter.trim()) params.loan_id = loanIdFilter.trim();
      const r = await axios.get(`${API_URL}/api/admin/loanbook-engine/shadow-rows`, { headers, params });
      setData(r.data);
    } catch (err) {
      toast.error('Failed to load shadow comparison rows');
    } finally {
      setLoading(false);
    }
  }, [token, selectedDate, source, driftOnly, loanIdFilter, page]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-6" data-testid="loanbook-shadow-page">
      <div className="flex items-center gap-3">
        <Link to="/admin/import"><Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" />Back to Data Import</Button></Link>
      </div>
      <div>
        <h1 className="text-2xl font-bold">LoanBook Engine — Shadow Comparison</h1>
        <p className="text-gray-500 text-sm mt-1">
          Per-loan, per-day: what the new engine computed vs. what the Excel-fed balance/interest tables already had.
          Observation only — nothing here changes what the portal shows.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle>Filters</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div>
            <Label>Date</Label>
            <Select value={selectedDate} onValueChange={(v) => { setSelectedDate(v); setPage(0); }}>
              <SelectTrigger className="w-[160px] mt-1"><SelectValue placeholder="All dates" /></SelectTrigger>
              <SelectContent>
                {dates.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Source</Label>
            <Select value={source || '__all__'} onValueChange={(v) => { setSource(v === '__all__' ? '' : v); setPage(0); }}>
              <SelectTrigger className="w-[140px] mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">LB &amp; RP</SelectItem>
                <SelectItem value="lb">LB only</SelectItem>
                <SelectItem value="rp">RP only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Loan ID</Label>
            <Input className="mt-1 w-[180px]" placeholder="e.g. 000101-LANG-1" value={loanIdFilter}
                   onChange={(e) => { setLoanIdFilter(e.target.value); setPage(0); }} />
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Checkbox id="drift-only" checked={driftOnly} onCheckedChange={(v) => { setDriftOnly(!!v); setPage(0); }} />
            <Label htmlFor="drift-only">Drift only</Label>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRows} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Results
            {data && (
              <Badge className={data.total > 0 && driftOnly ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}>
                {data.total.toLocaleString()} row(s)
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            {driftOnly ? 'Showing only rows where the new engine disagrees with the existing stored value by more than half a cent.' : 'Showing all shadow rows for the selected filters.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && <p className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading...</p>}
          {!loading && data?.rows?.length === 0 && (
            <p className="text-sm text-green-700 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />No rows match these filters{driftOnly ? ' — no drift found.' : '.'}</p>
          )}
          {!loading && data?.rows?.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Loan ID</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Computed Balance</TableHead>
                      <TableHead className="text-right">Existing Balance</TableHead>
                      <TableHead className="text-right">Computed Interest (mo)</TableHead>
                      <TableHead className="text-right">Existing Interest (mo)</TableHead>
                      <TableHead>Drift</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.rows.map((row) => (
                      <TableRow key={`${row.loan_id}-${row.source}-${row.date}`}>
                        <TableCell className="font-mono text-xs">{row.loan_id}</TableCell>
                        <TableCell className="uppercase text-xs">{row.source}</TableCell>
                        <TableCell>{row.date}</TableCell>
                        <TableCell className="text-right">{fmt(row.computed_closing_balance)}</TableCell>
                        <TableCell className={`text-right ${row.balance_drift ? 'text-amber-700 font-medium' : ''}`}>{fmt(row.existing_balance_timeseries_value)}</TableCell>
                        <TableCell className="text-right">{fmt(row.computed_interest_this_month)}</TableCell>
                        <TableCell className={`text-right ${row.interest_drift ? 'text-amber-700 font-medium' : ''}`}>{fmt(row.existing_interest_timeseries_value)}</TableCell>
                        <TableCell>
                          {(row.balance_drift || row.interest_drift) ? (
                            <Badge className="bg-amber-100 text-amber-800"><AlertTriangle className="h-3 w-3 mr-1" />
                              {row.balance_drift && row.interest_drift ? 'Balance & Interest' : row.balance_drift ? 'Balance' : 'Interest'}
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800">Match</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">Page {page + 1} of {totalPages}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                  <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoanbookShadowPage;
